<template>
  <div
    class="bucket-card-wrapper"
    :class="{ 'bucket-card-wrapper--locked': isLocked }"
    ref="bucketCard"
  >
    <div
      class="card buckets-card"
      :class="{ 'buckets-card--locked': isLocked }"
      :style="{
        '--bucket-color': bucketStat?.bucketOption?.colorHex || '#3e3efe',
      }"
    >
      <div class="card-body">
        <!-- Lock overlay -->
        <div v-if="isLocked" class="lock-overlay">
          <Icon icon="mdi:lock" class="lock-overlay__icon" />
          <span class="lock-overlay__title">Bucket Locked</span>
          <span v-if="lockReason" class="lock-overlay__reason">{{ lockReason }}</span>
        </div>

        <div v-if="hasOverride" class="override-indicator" title="Override active">
          <Icon icon="mdi:lock-open-variant" />
        </div>

        <div class="card-content" :class="{ 'card-content--blurred': isLocked }">
          <div v-if="canEditBucket && !isLocked" class="card-menu">
            <PopoverMenu :items="menuItems" trigger-icon="mdi:dots-vertical" placement="bottom" />
          </div>

          <div
            class="icon-container"
            :style="{
              backgroundColor: hexToRgba(bucketStat?.bucketOption?.colorHex || '#3e3efe', 0.1),
            }"
          >
            <Icon
              icon="fa7-solid:bucket"
              class="bucket-icon"
              :style="{
                color: bucketStat?.bucketOption?.colorHex || '#3e3efe',
              }"
            />
          </div>

          <div
            class="count-name-section"
            @mouseenter="showContactTooltip = true"
            @mouseleave="showContactTooltip = false"
          >
            <h3 class="count">
              <router-link
                v-if="!isLocked"
                :to="{
                  name: BUCKETS_ROUTE,
                  params: {
                    bucket: bucketStat?.bucketOption.slug,
                  },
                  query: { status: 'past' },
                }"
                class="count-link"
              >
                {{ bucketStat.count }}
                <Icon v-if="showContactTooltip" icon="radix-icons:people" class="contact-icon" />
              </router-link>
              <span v-else class="count-link count-link--locked">
                {{ bucketStat.count }}
              </span>
            </h3>

            <!-- Bucket Name -->
            <h6 class="name">
              <router-link
                v-if="!isLocked"
                :to="{
                  name: BUCKETS_ROUTE,
                  params: {
                    bucket: bucketStat?.bucketOption.slug,
                  },
                  query: { status: 'due' },
                }"
                class="name-link"
              >
                {{ bucketStat?.bucketOption.name }}
              </router-link>
              <span v-else class="name-link name-link--locked">
                {{ bucketStat?.bucketOption.name }}
              </span>
            </h6>

            <div v-if="showContactTooltip" class="contact-tooltip">
              <div class="tooltip-text">Click to view all {{ bucketStat.count }} contacts</div>
            </div>
          </div>

          <div class="divider-wrapper">
            <div class="divider"></div>
            <div class="animated-line" :class="{ active: showContactTooltip }"></div>
          </div>

          <div class="status-indicators">
            <div
              class="status-item overdue-item"
              @click="navigateToStatus('past')"
              @mouseenter="hoveredStatus = 'past'"
              @mouseleave="hoveredStatus = null"
            >
              <div class="status-icon overdue-icon" :class="{ glowing: hoveredStatus === 'past' }">
                <Icon icon="mdi:clock-alert-outline" />
                <span
                  class="status-count overdue-count"
                  :class="{ hovered: hoveredStatus === 'past' }"
                >
                  {{ bucketStat.past }}
                </span>
              </div>
              <div v-if="hoveredStatus === 'past'" class="status-tooltip overdue-tooltip">
                <div class="tooltip-text">{{ bucketStat.past }} overdue tasks</div>
              </div>
            </div>

            <div
              class="status-item due-item"
              @click="handleDueClick"
              @mouseenter="hoveredStatus = 'due'"
              @mouseleave="hoveredStatus = null"
            >
              <div
                class="status-icon due-icon"
                :class="{
                  glowing: hoveredStatus === 'due' || hasDueTasks,
                  pulsing: hasDueTasks,
                }"
              >
                <Icon icon="mdi:calendar-today" />
                <span class="status-count due-count" :class="{ hovered: hoveredStatus === 'due' }">
                  {{ bucketStat.due }}
                  <span
                    v-if="showDueNotification"
                    class="due-notification-badge"
                    @click.stop="dismissNotification"
                  >
                    <span class="badge-pulse"></span>
                    <span class="badge-dot"></span>
                  </span>
                </span>
              </div>
              <div v-if="hoveredStatus === 'due'" class="status-tooltip due-tooltip">
                <div class="tooltip-text">{{ bucketStat.due }} tasks due today</div>
              </div>
            </div>

            <div
              class="status-item upcoming-item"
              @click="navigateToStatus('future')"
              @mouseenter="hoveredStatus = 'future'"
              @mouseleave="hoveredStatus = null"
            >
              <div
                class="status-icon upcoming-icon"
                :class="{ glowing: hoveredStatus === 'future' }"
              >
                <Icon icon="mdi:calendar-clock" />
                <span
                  class="status-count upcoming-count"
                  :class="{ hovered: hoveredStatus === 'future' }"
                >
                  {{ bucketStat.future }}
                </span>
              </div>
              <div v-if="hoveredStatus === 'future'" class="status-tooltip upcoming-tooltip">
                <div class="tooltip-text">{{ bucketStat.future }} upcoming tasks</div>
              </div>
            </div>

            <div
              v-if="unreadCount > 0"
              class="status-item unread-item"
              @click="navigateToBucket"
              @mouseenter="hoveredStatus = 'unread'"
              @mouseleave="hoveredStatus = null"
            >
              <span class="unread-icon-wrapper">
                <Icon icon="mdi:comment-text" class="unread-icon" />
                <span class="unread-count-badge">{{ unreadCount }}</span>
              </span>
              <div v-if="hoveredStatus === 'unread'" class="status-tooltip unread-tooltip">
                <div class="tooltip-text">
                  {{ unreadCount }} unread message{{ unreadCount === 1 ? "" : "s" }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { usePermissions } from "@/composables/usePermissions";
import { PopoverMenu } from "@/components/ui";
import { BUCKETS_ROUTE } from "@/router/routes";
import { Icon } from "@iconify/vue";

defineOptions({
  name: "BucketListCard",
});

const props = defineProps({
  bucketStat: {
    type: Object,
    required: true,
  },
  account: {
    type: Object,
    required: true,
  },
  isLocked: {
    type: Boolean,
    default: false,
  },
  lockReason: {
    type: String,
    default: "",
  },
  hasOverride: {
    type: Boolean,
    default: false,
  },
  unreadCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["edit-bucket", "delete-bucket"]);

const router = useRouter();
const { canManageContacts } = usePermissions();

// Refs
const bucketCard = ref(null);
const hoveredStatus = ref(null);
const showContactTooltip = ref(false);

// Computed properties
const bucketSlug = computed(() => {
  return props.bucketStat?.bucketOption?.slug || "";
});

const showDueNotification = computed(() => {
  if (!props.bucketStat?.due || props.bucketStat.due === 0) {
    return false;
  }

  const dismissedData = getDismissedNotification();
  if (!dismissedData) {
    return true;
  }

  return props.bucketStat.due > dismissedData.dueCount;
});

const hasDueTasks = computed(() => {
  return props.bucketStat?.due && props.bucketStat.due > 0;
});

const canEditBucket = computed(() => {
  if (!props.account) return false;

  if (canManageContacts.value) {
    return true;
  }

  const bucketOption = props.bucketStat?.bucketOption;
  return bucketOption?.createdById === props.account.id;
});

const menuItems = computed(() => {
  if (!props.bucketStat?.bucketOption) {
    return [];
  }
  const bucketOption = props.bucketStat.bucketOption;
  if (!bucketOption) return [];

  return [
    {
      label: "Edit Bucket",
      icon: "mdi:pencil-outline",
      onClick: () => emit("edit-bucket", bucketOption),
    },
    {
      label: "Delete",
      icon: "mdi:delete-outline",
      onClick: () => emit("delete-bucket", bucketOption),
      danger: true,
    },
  ];
});

// Methods
const navigateToStatus = (status) => {
  if (props.isLocked) return;
  router.push({
    name: BUCKETS_ROUTE,
    params: {
      bucket: bucketSlug.value,
    },
    query: { status },
  });
};

const navigateToBucket = () => {
  if (props.isLocked) return;
  router.push({
    name: BUCKETS_ROUTE,
    params: {
      bucket: bucketSlug.value,
    },
  });
};

const handleDueClick = () => {
  if (props.isLocked) return;
  if (showDueNotification.value) {
    dismissNotification();
  }
  navigateToStatus("due");
};

const dismissNotification = () => {
  const dismissedData = {
    dismissedAt: Date.now(),
    dueCount: props.bucketStat.due,
  };
  const storageKey = `bucket-due-dismissed-${bucketSlug.value}`;
  try {
    localStorage.setItem(storageKey, JSON.stringify(dismissedData));
  } catch (error) {
    console.warn("Failed to save notification dismissal to localStorage:", error);
  }
};

const getDismissedNotification = () => {
  const storageKey = `bucket-due-dismissed-${bucketSlug.value}`;
  try {
    const data = localStorage.getItem(storageKey);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    console.warn("Failed to read notification dismissal from localStorage:", error);
  }
  return null;
};

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(62, 62, 254, ${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
</script>

<style scoped>
.bucket-card-wrapper {
  flex: 0 0 auto;
  width: 240px;
  min-width: 240px;
}

.buckets-card {
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  box-shadow:
    0px 1px 3px rgba(0, 0, 0, 0.1),
    0px 1px 2px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.buckets-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--bucket-color, #3e3efe);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.buckets-card:hover {
  transform: translateY(-4px);
  box-shadow:
    0px 10px 25px rgba(0, 0, 0, 0.1),
    0px 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #d1d5db;
}

.buckets-card:hover::before {
  opacity: 1;
}

.buckets-card .card-body {
  padding: 16px 14px 12px 14px;
  position: relative;
}

.card-menu {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
}

.card-menu :deep(.popover-menu__trigger .iconify) {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  margin-bottom: 12px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.buckets-card:hover .icon-container {
  transform: scale(1.05);
}

.bucket-icon {
  font-size: 14px;
  width: 14px;
  height: 14px;
}

.count-name-section {
  position: relative;
  margin-bottom: 16px;
}

.divider-wrapper {
  position: relative;
  margin: 8px 0;
  height: 1px;
}

.divider {
  height: 1px;
  background: #e5e7eb;
  width: 100%;
}

.animated-line {
  position: absolute;
  top: 0;
  left: 0;
  height: 2px;
  background: linear-gradient(90deg, #3e3efe 0%, #3e3efe 100%);
  width: 0;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 1px;
}

.animated-line.active {
  width: 100%;
}

.contact-icon {
  font-size: 20px;
  width: 20px;
  height: 20px;
  color: #3e3efe;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.contact-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 20;
  white-space: nowrap;
  animation: fadeIn 0.3s ease;
}

.contact-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #ffffff;
}

.tooltip-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
}

.count {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.2;
  color: #111827;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-link {
  color: inherit;
  text-decoration: none;
  transition: color 0.3s ease;
}

.count-link:hover {
  color: #3e3efe;
}

.buckets-card:hover .count {
  color: #3e3efe;
}

.name {
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0 0 16px 0;
  color: #6b7280;
  line-height: 1.4;
}

.name-link {
  color: inherit;
  text-decoration: none;
  transition: color 0.3s ease;
}

.name-link:hover {
  color: #374151;
}

.status-indicators {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  margin-top: 0;
  margin-bottom: 0;
}

.status-item {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border-radius: 8px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.status-item:hover {
  transform: translateY(-2px);
}

.status-icon {
  min-width: auto;
  height: 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 2px 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  position: relative;
}

.status-icon.glowing {
  box-shadow:
    0 0 12px rgba(0, 0, 0, 0.2),
    0 0 20px rgba(0, 0, 0, 0.15);
  transform: scale(1.05);
}

.status-item:hover .status-icon {
  transform: scale(1.05);
}

.overdue-icon {
  background-color: #fee2e2;
  color: #dc2626;
  border: 1px solid rgba(220, 38, 38, 0.2);
}

.overdue-icon.glowing {
  box-shadow:
    0 0 12px rgba(220, 38, 38, 0.4),
    0 0 20px rgba(220, 38, 38, 0.3);
}

.due-icon {
  background-color: #d1fae5;
  color: #059669;
  border: 1px solid rgba(5, 150, 105, 0.2);
}

.due-icon.glowing {
  box-shadow:
    0 0 12px rgba(5, 150, 105, 0.4),
    0 0 20px rgba(5, 150, 105, 0.3);
}

.due-icon.pulsing {
  animation: pulseScale 2s ease-in-out infinite;
}

@keyframes pulseScale {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
  100% {
    transform: scale(1);
  }
}

.upcoming-icon {
  background-color: #fed7aa;
  color: #d97706;
  border: 1px solid rgba(217, 119, 6, 0.2);
}

.upcoming-icon.glowing {
  box-shadow:
    0 0 12px rgba(217, 119, 6, 0.4),
    0 0 20px rgba(217, 119, 6, 0.3);
}

.unread-icon-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #10b981;
  transition: background-color 0.2s ease;
}

.unread-icon {
  font-size: 14px;
  width: 14px;
  height: 14px;
  color: #fff;
}

.unread-item:hover .unread-icon-wrapper {
  background-color: #059669;
}

.unread-count-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  border-radius: 999px;
  border: 1.5px solid #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  animation: unreadBadgeBlink 1.4s ease-in-out infinite;
}

@keyframes unreadBadgeBlink {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

.status-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 20;
  white-space: nowrap;
  animation: fadeIn 0.3s ease;
}

.status-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #ffffff;
}

.status-tooltip .tooltip-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
}

.status-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: inherit;
  padding: 0;
  margin: 0;
  text-align: center;
  transition: all 0.2s ease;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  line-height: 1;
}

.overdue-count {
  color: #dc2626;
}

.due-count {
  color: #059669;
}

.upcoming-count {
  color: #d97706;
}

.status-count.hovered {
  transform: scale(1.15);
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
}

/* Due Notification Badge */
.due-count-wrapper {
  position: relative;
}

.due-notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 10px;
  height: 10px;
  z-index: 3;
  cursor: pointer;
}

.badge-pulse {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: #ef4444;
  animation: pulse 2s infinite;
}

.badge-dot {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #dc2626;
  box-shadow: 0 0 4px rgba(220, 38, 38, 0.6);
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.4);
    opacity: 0.6;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Link styles */
.count-link,
.name-link {
  display: block;
}

.count-link:hover,
.name-link:hover {
  text-decoration: none;
}

/* Lock styles */
.buckets-card--locked {
  position: relative;
}

.buckets-card--locked:hover {
  transform: none;
  box-shadow:
    0px 1px 3px rgba(0, 0, 0, 0.1),
    0px 1px 2px rgba(0, 0, 0, 0.06);
}

.card-content--blurred {
  filter: blur(4px);
  pointer-events: none;
  user-select: none;
}

.lock-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 16px;
}

.lock-overlay__icon {
  font-size: 28px;
  color: #6b7280;
}

.lock-overlay__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.lock-overlay__reason {
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
  padding: 0 12px;
  line-height: 1.3;
}

.override-indicator {
  position: absolute;
  top: 12px;
  left: 12px;
  color: #059669;
  font-size: 18px;
  z-index: 5;
}

.count-link--locked,
.name-link--locked {
  cursor: not-allowed;
  pointer-events: none;
}

.count-link--locked:hover,
.name-link--locked:hover {
  color: inherit;
}
</style>
